/**
 * The shelf the jukebox plays off, and the arithmetic of moving along it
 * (SPEC-014 PR-14.1).
 *
 * Pure, importing nothing: no `Audio`, no `window`, no fetch. What a track IS,
 * which one comes next and how long it has been playing are questions with
 * answers that do not depend on a browser being present, and holding them here
 * is what lets the whole transport be tested without one — jsdom has no media
 * pipeline at all, so anything that reaches an `<audio>` element is unprovable
 * outside Playwright.
 *
 * THE SHELF IS FETCHED, NOT AUTHORED HERE. `scripts/build-soundtrack.js` writes
 * `assets/soundtrack/soundtrack.json` out of the delivery, and the durations in
 * it are MEASURED off the frame table rather than typed by anybody — the same
 * argument `carArt.js` makes about `media.widths`. So this module's first job is
 * the boundary check: what arrives is JSON off the network, and on this origin a
 * missing file is answered with the app shell and a 200 (see
 * `soundArtefacts.test.js`), which parses into something that is not a shelf.
 */

/**
 * How a version's label is to be read: a tempo in BPM, a running time, or a word
 * the delivery itself supplied. Anything else a manifest offers is read as a
 * NAME and printed as it stands, which is the only kind that is safe to get
 * wrong — the other two get a sentence composed around them.
 */
const VARIANT_KINDS = new Set(['tempo', 'length', 'name']);

/** Where `build-soundtrack.js` writes, and the fallback if a manifest omits it. */
const SOUNDTRACK_BASE = '/assets/soundtrack';

/**
 * A row of the manifest, as the app wants it: addresses rather than filenames.
 *
 * The URL composition happens ONCE, here, for the reason `carArt.js` composes
 * its `srcset` in one place — a second spelling of where the build wrote its
 * files is a directory that exists only in the app, and every request 404s while
 * the markup still looks right.
 */
function toTrack(row, base) {
    const coverWidths = Array.isArray(row.coverWidths) ? row.coverWidths : [];

    return {
        id: row.id,
        title: row.title,
        /* Which recording of that title this is, or null where the title was
           delivered once. Two fields rather than one because the app renders
           them differently: a NAME the delivery supplied ("Remix") is printed as
           it stands, while a TEMPO or a LENGTH — the measured labels for the
           takes that arrived as `(1)` and `(2)` — are printed inside a sentence,
           because a bare number under a title reads as a fault. */
        /* What this recording sounds like, in a word — the build's reading of
           four numbers measured off the audio, since the delivery names no genre
           anywhere. Null is an ordinary state: a manifest written before styles,
           or a track the measurement has no reading for. */
        style: typeof row.style === 'string' && row.style ? row.style : null,
        variant: typeof row.variant === 'string' && row.variant ? row.variant : null,
        variantKind: VARIANT_KINDS.has(row.variantKind) ? row.variantKind : 'name',
        /* What makes two rows versions of ONE thing. Falls back to the id, so a
           manifest that predates families reads as every track being its own —
           which is exactly what it was. */
        family: typeof row.family === 'string' && row.family ? row.family : row.id,
        src: `${base}/${row.file}`,
        seconds: Number.isFinite(row.seconds) && row.seconds > 0 ? row.seconds : 0,
        /* A track with no cover is a state this is built to have: the picture
           comes out of the delivered file's own ID3 tag, and a future drop may
           simply not carry one. The sheet draws a plate instead. */
        cover: coverWidths.length > 0
            ? {
                src: `${base}/${row.id}-${coverWidths[coverWidths.length - 1]}.webp`,
                srcSet: coverWidths.map((width) => `${base}/${row.id}-${width}.webp ${width}w`).join(', '),
            }
            : null,
    };
}

/**
 * Every playable row of a manifest, in the order the build wrote them.
 *
 * A row missing an `id`, a `title` or a `file` is DROPPED rather than rendered
 * as a gap or thrown over: the three of them are what makes a row addressable,
 * and a jukebox with seven tracks on the shelf is a working jukebox where one
 * that threw during render is a black screen. Anything that is not a manifest at
 * all — the app shell, `null`, an array — comes back as an empty shelf, which
 * the sheet already has to draw for the reader whose fetch failed.
 */
export function readSoundtrack(manifest) {
    const rows = manifest && Array.isArray(manifest.tracks) ? manifest.tracks : [];
    const base = (manifest && typeof manifest.base === 'string' && manifest.base) || SOUNDTRACK_BASE;

    return rows
        .filter((row) => row && typeof row.id === 'string' && typeof row.title === 'string' && typeof row.file === 'string')
        .map((row) => toTrack(row, base));
}

/**
 * A deterministic shuffle of `count` positions.
 *
 * Fisher-Yates over a seeded generator rather than `Math.random`, and the seed
 * is the caller's, so this stays a pure function of its arguments and the order
 * a reader is given can be asserted in a unit test. The application passes the
 * clock; nothing here reads one.
 *
 * `mulberry32` is the generator, chosen because it is eight lines and has no
 * dependency. A jukebox does not need a good PRNG — it needs one that does not
 * produce the same order twice in a session, and any of them clears that bar.
 */
function seededOrder(count, seed) {
    const order = Array.from({ length: count }, (unused, index) => index);
    let state = seed >>> 0;

    const next = () => {
        state = (state + 0x6d2b79f5) >>> 0;
        let mixed = Math.imul(state ^ (state >>> 15), 1 | state);

        mixed = (mixed + Math.imul(mixed ^ (mixed >>> 7), 61 | mixed)) ^ mixed;

        return ((mixed ^ (mixed >>> 14)) >>> 0) / 4294967296;
    };

    for (let at = count - 1; at > 0; at -= 1) {
        const swap = Math.floor(next() * (at + 1));

        [order[at], order[swap]] = [order[swap], order[at]];
    }

    return order;
}

/**
 * Deal a shuffled order out so that no two members of one family sit together.
 *
 * THE PROBLEM THIS SOLVES IS THE SHELF'S OWN. Twenty recordings are twelve
 * titles: `4.1 Seconds` arrived three times and `The Duel at Sundown` three
 * times, and a fair shuffle over twenty will happily hand a listener two takes
 * of the same song back to back. That is the one failure a shelf of versions can
 * have that a shelf of distinct tracks cannot, and it reads as the jukebox being
 * stuck rather than as chance.
 *
 * The greedy is the standard one for this problem and it is OPTIMAL: at every
 * step take from the fullest family that is not the one just played. If any
 * arrangement with no two neighbours alike exists, this finds one — and one
 * exists whenever no family holds more than half the shelf, which at three of
 * twenty is not close.
 *
 * `startingAfter` is the family of a track already placed ahead of this run, so
 * the SEAM between it and the first thing dealt here is held to the same rule.
 * Without it the one join nothing checks is the one right at the front, which is
 * the join a listener hears first.
 */
function spreadFamilies(order, families, startingAfter = null) {
    const queues = new Map();

    order.forEach((index) => {
        const key = families[index];
        const queue = queues.get(key);

        if (queue) {
            queue.push(index);
        } else {
            queues.set(key, [index]);
        }
    });

    const spread = [];
    let previous = startingAfter;

    while (spread.length < order.length) {
        const fullest = [...queues.entries()]
            .filter(([key, members]) => members.length > 0 && key !== previous)
            .sort((left, right) => right[1].length - left[1].length)[0];

        // Only reachable when one family holds more than half of what is left,
        // which this shelf cannot do. Its remainder goes on the end below rather
        // than being dropped: a track missing from the running order is worse
        // than two of a kind in a row.
        if (!fullest) {
            break;
        }

        const [key, members] = fullest;

        spread.push(members.shift());
        previous = key;
    }

    queues.forEach((members) => spread.push(...members));

    return spread;
}

/**
 * The order the shelf is played in.
 *
 * `startAt` is held FIRST rather than left wherever the shuffle put it, and that
 * is the whole reason this takes it. Pressing shuffle while something is playing
 * must not change what is playing — a jukebox that jumped to another track the
 * moment you asked for a different order would read as a bug, and it is the one
 * thing a reader will notice about this function.
 *
 * `families` is optional and only means anything under shuffle. IN ORDER is left
 * exactly as the build wrote it, deliberately: that order groups a title's
 * versions together because the SHELF is a catalogue and somebody looking for
 * the other take should find it under the first. A reader who turns shuffle off
 * on a shelf that visibly groups them is asking for them in that order.
 */
export function playOrder({
    count, isShuffled = false, startAt = 0, seed = 1, families = null,
}) {
    if (count <= 0) {
        return [];
    }

    if (!isShuffled) {
        return Array.from({ length: count }, (unused, index) => index);
    }

    const held = Math.min(Math.max(startAt, 0), count - 1);
    const rest = seededOrder(count, seed).filter((index) => index !== held);

    if (!families) {
        return [held, ...rest];
    }

    return [held, ...spreadFamilies(rest, families, families[held])];
}

/**
 * The next position along an order, wrapping.
 *
 * IT WRAPS RATHER THAN STOPPING, which is a product decision and not a default.
 * This plays under an app somebody is using for something else; a shelf that ran
 * out after twenty-four minutes and went quiet would leave them wondering what
 * they had broken. The button to stop it is the one that says stop.
 *
 * A position that is not on the order — the shelf changed under a running
 * player — resolves to the first, rather than to `-1` and a track that is
 * `undefined`.
 */
export function stepOrder(order, current, by) {
    if (order.length === 0) {
        return -1;
    }

    const at = order.indexOf(current);

    if (at === -1) {
        return order[0];
    }

    return order[(at + by + order.length) % order.length];
}

/**
 * A duration as a clock reads it.
 *
 * Minutes are NOT padded and seconds always are: `3:05`, never `03:05`, because
 * that is how every player anybody has used prints it. Anything that is not a
 * finite number — a track whose metadata has not loaded — is a dash rather than
 * `NaN:NaN` or a confident `0:00`, which would claim the track is over.
 */
export function formatTrackTime(seconds) {
    if (!Number.isFinite(seconds) || seconds < 0) {
        return '–:––';
    }

    const whole = Math.floor(seconds);

    return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}

/** How far through a track playback is, as a fraction. Never past its ends. */
export function trackProgress({ seconds, duration }) {
    if (!Number.isFinite(seconds) || !Number.isFinite(duration) || duration <= 0) {
        return 0;
    }

    return Math.min(1, Math.max(0, seconds / duration));
}

export { SOUNDTRACK_BASE };
